import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Phone, MessageCircle, CheckCircle, ArrowRight, Clock, Shield, Sparkles, MapPin } from "lucide-react";
import Header from "./Header";
import Footer from "./Footer";
import MobilePhoneButton from "./MobilePhoneButton";
import CookieConsent from "./CookieConsent";
import { navData } from "../../data/mockData";
import { usePageMeta, faqSchema, SERVICE_AREAS, PROVIDER } from "../../lib/seo";
import { requestQuote } from "../../lib/quoteIntent";

/* ── Service Page Template Component ── */

const AREA_LINKS = [
  { name: "Cardiff", to: "/" },
  { name: "Barry", to: "/cleaning-services-barry" },
  { name: "Penarth", to: "/cleaning-services-penarth" },
];

const AreasStrip = () => (
  <section className="py-10 bg-white border-t border-slate-100" data-testid="service-areas-strip">
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row sm:items-center gap-4">
      <p className="flex items-center gap-2 font-semibold text-slate-900"><MapPin className="w-5 h-5 text-sky-600" /> Areas we cover:</p>
      <ul className="flex flex-wrap gap-2">
        {AREA_LINKS.map((a) => (
          <li key={a.name}>
            <Link to={a.to} onClick={() => window.scrollTo({ top: 0 })} className="inline-block rounded-full border border-sky-200 px-4 py-1.5 text-sm font-semibold text-sky-700 hover:bg-sky-50" data-testid={`area-link-${a.name.toLowerCase()}`}>
              Cleaning in {a.name}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  </section>
);

const PricingGuide = ({ pricingInfo, priceList }) => (
  <section className="py-16 bg-white">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Pricing Guide</h2>
      <div className="bg-gradient-to-br from-sky-50 to-white border-2 border-sky-100 rounded-xl p-8">
        <div className="grid md:grid-cols-3 gap-6 mb-6">
          <div className="text-center">
            <Clock className="w-8 h-8 text-sky-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-slate-900">{pricingInfo.duration}</div>
            <div className="text-sm text-slate-600">Typical Duration</div>
          </div>
          <div className="text-center">
            <Sparkles className="w-8 h-8 text-sky-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-slate-900">{pricingInfo.price}</div>
            <div className="text-sm text-slate-600">Starting Price</div>
          </div>
          <div className="text-center">
            <Shield className="w-8 h-8 text-sky-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-slate-900">48h</div>
            <div className="text-sm text-slate-600">Missed something? We'll put it right</div>
          </div>
        </div>
        <p className="text-sm text-slate-600 text-center border-t border-sky-200 pt-4">{pricingInfo.note}</p>
        {priceList && (
          <ul className="mt-6 divide-y divide-sky-100 rounded-lg border border-sky-100 bg-white" data-testid="service-price-list">
            {priceList.map((row, i) => (
              <li key={row.item} className="flex items-center justify-between gap-4 px-5 py-3 text-sm" data-testid={`service-price-row-${i}`}>
                <span className="text-slate-700">{row.item}</span>
                <span className="font-bold text-slate-900 whitespace-nowrap">{row.price}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  </section>
);

const ServicePageTemplate = ({
  pageTitle,
  metaDescription,
  heroTitle,
  heroSubtitle,
  heroImage,
  heroImageAlt,
  secondaryImage,
  secondaryImageAlt,
  serviceDescription,
  includedTitle = "What's Included",
  whatIncluded,
  pricingInfo,
  priceList,
  faqs,
  testimonial,
  relatedServices,
  extraSection,
  quoteService,
  areas = SERVICE_AREAS,
  ctaLabel = "Get a Free Quote",
  onCta
}) => {
  const navigate = useNavigate();
  const url = `https://neatwavecleaning.co.uk${typeof window !== "undefined" ? window.location.pathname : ""}`;

  usePageMeta({
    title: pageTitle,
    description: metaDescription,
    schemas: [
      {
        "@context": "https://schema.org",
        "@type": "Service",
        name: heroTitle,
        description: metaDescription,
        url,
        areaServed: areas.map((name) => ({ "@type": "City", name })),
        provider: PROVIDER,
        ...(pricingInfo?.price?.includes("£") ? { offers: { "@type": "Offer", priceCurrency: "GBP", description: pricingInfo.price } } : {}),
      },
      ...(faqs?.length ? [faqSchema(faqs)] : []),
    ],
  });

  const goToQuote = () => requestQuote(navigate, quoteService);

  return (
    <div className="min-h-screen bg-white font-inter">
      <Header scrollToQuote={goToQuote} />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-sky-50 to-white pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6" data-testid="service-page-h1">
                {heroTitle}
              </h1>
              <p className="text-lg text-slate-600 mb-8">
                {heroSubtitle}
              </p>
              <div className="flex flex-wrap gap-4">
                <button
                  onClick={onCta || goToQuote}
                  className="inline-flex items-center bg-sky-600 hover:bg-sky-700 text-white font-semibold px-8 py-3 rounded-lg shadow-lg transition-colors"
                  data-testid="service-hero-quote-btn"
                >
                  {ctaLabel}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </button>
                <a
                  href={`tel:${navData.phoneLink}`}
                  className="inline-flex items-center bg-white hover:bg-slate-50 text-sky-600 font-semibold px-8 py-3 rounded-lg border-2 border-sky-600 transition-colors"
                  data-testid="service-hero-call-btn"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Call {navData.phone}
                </a>
                <a
                  href={navData.whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center bg-green-600 hover:bg-green-700 text-white font-semibold px-6 py-3 rounded-lg shadow transition-colors"
                  data-testid="service-hero-whatsapp-btn"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  WhatsApp
                </a>
              </div>
            </div>
            {heroImage && (
              <div className="space-y-6">
                <div className="rounded-xl overflow-hidden shadow-2xl">
                  <img 
                    src={heroImage} 
                    alt={heroImageAlt || heroTitle}
                    className="w-full h-auto"
                    fetchPriority="high"
                  />
                </div>
                {secondaryImage && (
                  <div className="rounded-xl overflow-hidden shadow-2xl">
                    <img
                      src={secondaryImage}
                      alt={secondaryImageAlt || heroTitle}
                      className="w-full h-auto"
                      loading="lazy"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Service Description */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none text-slate-600 [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:text-slate-900 [&_h2]:mt-8 [&_h2]:mb-3 [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:text-slate-900 [&_h3]:mt-6 [&_h3]:mb-2 [&_a]:text-sky-700 [&_a]:font-semibold [&_a]:underline">
            {serviceDescription}
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">
            {includedTitle}
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whatIncluded.map((item) => (
              <div key={item.title} className="bg-white p-6 rounded-lg shadow-md">
                <CheckCircle className="w-8 h-8 text-green-500 mb-3" />
                <h3 className="font-semibold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-sm text-slate-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {pricingInfo && <PricingGuide pricingInfo={pricingInfo} priceList={priceList} />}

      {/* FAQs */}
      {faqs && faqs.length > 0 && (
        <section className="py-16 bg-slate-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">
              Frequently Asked Questions
            </h2>
            <div className="space-y-4">
              {faqs.map((faq) => (
                <div key={faq.q} className="bg-white p-6 rounded-lg shadow-md">
                  <h3 className="font-semibold text-slate-900 mb-2">{faq.q}</h3>
                  <p className="text-slate-600">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Testimonial */}
      {testimonial && (
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="bg-sky-50 p-8 rounded-xl">
              <p className="text-xl text-slate-700 italic mb-4">"{testimonial.text}"</p>
              <p className="font-semibold text-slate-900">— {testimonial.author}</p>
            </div>
          </div>
        </section>
      )}

      {extraSection}

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-sky-600 to-sky-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-xl mb-8">Get your free quote today - no obligation, instant estimate</p>
          <div className="flex flex-wrap justify-center gap-4">
            <button
              onClick={goToQuote}
              className="inline-flex items-center bg-white text-sky-600 hover:bg-slate-100 font-semibold px-8 py-3 rounded-lg shadow-lg transition-colors"
              data-testid="service-cta-quote-btn"
            >
              Get a Free Quote
              <ArrowRight className="w-5 h-5 ml-2" />
            </button>
            <a
              href={`tel:${navData.phoneLink}`}
              className="inline-flex items-center bg-sky-800 hover:bg-sky-900 text-white font-semibold px-8 py-3 rounded-lg shadow-lg transition-colors"
              data-testid="service-cta-call-btn"
            >
              <Phone className="w-5 h-5 mr-2" />
              Call NeatWave
            </a>
            <a
              href={navData.whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center bg-green-600 hover:bg-green-700 text-white font-semibold px-8 py-3 rounded-lg shadow-lg transition-colors"
              data-testid="service-cta-whatsapp-btn"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              WhatsApp NeatWave
            </a>
          </div>
        </div>
      </section>

      {/* Related Services */}
      {relatedServices && relatedServices.length > 0 && (
        <section className="py-16 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">
              You May Also Be Interested In
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {relatedServices.map((service) => (
                <Link
                  key={service.url}
                  to={service.url}
                  onClick={() => window.scrollTo({ top: 0 })}
                  className="block bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-shadow"
                  data-testid={`related-service-${service.url.replace(/\//g, "")}`}
                >
                  <h3 className="font-semibold text-slate-900 mb-2">{service.title}</h3>
                  <p className="text-sm text-slate-600 mb-4">{service.description}</p>
                  <span className="text-sky-600 font-medium inline-flex items-center">
                    Learn More
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <AreasStrip />
      <Footer />
      <MobilePhoneButton phoneLink={navData.phoneLink} />
      <CookieConsent />
    </div>
  );
};

export default ServicePageTemplate;
